import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  ColorPicker,
  Field,
  KanbanTag,
  Page,
  PageHeader,
  Select,
} from '@evogard/ui';
import type { RetentionLabel } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import {
  RETENTION_KEY,
  useInvalidateRetention,
  useRetentionTenants,
} from './useRetention.js';

/**
 * As etiquetas do Kanban Cancelamento, por base — o `EtiquetasAdmin` do
 * legado. Apagar tira a etiqueta de todos os cartões.
 */
export function LabelsScreen() {
  const tenants = useRetentionTenants();
  const { data: context } = useSessionContext();
  const [tenant, setTenant] = useState('');
  useEffect(() => {
    if (!tenant && context) setTenant(context.currentTenant.slug);
  }, [tenant, context]);
  const invalidate = useInvalidateRetention();

  const query = useQuery({
    queryKey: [...RETENTION_KEY, 'etiquetas', tenant],
    enabled: tenant !== '',
    queryFn: async () =>
      ok(
        await api.retention.labels({ params: { tenant } }),
        200,
        'Não foi possível carregar.',
      ),
  });
  const [name, setName] = useState('');
  const [color, setColor] = useState<RetentionLabel['color']>('cinza');

  const create = useMutation({
    mutationFn: async () =>
      ok(
        await api.retention.createLabel({ params: { tenant }, body: { name, color } }),
        201,
        'Não foi possível criar a etiqueta.',
      ),
    onSuccess: () => {
      setName('');
      invalidate();
    },
  });
  const archive = useMutation({
    mutationFn: async (id: number) => {
      const r = await api.retention.archiveLabel({ params: { tenant, id }, body: {} });
      if (r.status !== 204) throw new Error('Não foi possível apagar a etiqueta.');
    },
    onSuccess: invalidate,
  });

  return (
    <Page>
      <PageHeader title="Etiquetas">
        As etiquetas que se põem nos cartões do Kanban Cancelamento.
      </PageHeader>
      <Select label="Base" value={tenant} onChange={(e) => setTenant(e.target.value)}>
        {tenants.map((t) => (
          <option key={t.slug} value={t.slug}>
            {t.name}
          </option>
        ))}
      </Select>
      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
      <Card>
        {(query.data?.items ?? []).length === 0 ? (
          <p className="text-sm text-texto-suave">Nenhuma etiqueta nesta base.</p>
        ) : (
          <ul className="grid gap-2">
            {query.data!.items.map((l) => (
              <li key={l.id} className="flex items-center justify-between gap-3">
                <KanbanTag color={l.color}>{l.name}</KanbanTag>
                <Button
                  size="sm"
                  variant="discreto"
                  loading={archive.isPending}
                  onClick={() => {
                    if (
                      window.confirm(`Apagar a etiqueta "${l.name}" de todos os cartões?`)
                    ) {
                      archive.mutate(l.id);
                    }
                  }}
                >
                  Apagar
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <Field
          label="Nova etiqueta"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <ColorPicker
          value={color}
          onChange={(c) => setColor(c as RetentionLabel['color'])}
        />
        <Button type="submit" disabled={!name.trim()} loading={create.isPending}>
          Criar
        </Button>
      </form>
      {(create.isError || archive.isError) && (
        <Alert tone="erro">{(create.error ?? archive.error)!.message}</Alert>
      )}
    </Page>
  );
}
